'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Code, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomJsonGeneratorContent } from '../../data/random-json-generator-content';
import Link from 'next/link';

type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];

interface JsonObject {
    [key: string]: JsonValue;
}

interface JsonField {
    key: string;
    type: 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array';
    children?: JsonField[];
    min?: number;
    max?: number;
    items?: number;
    depth?: number;
}

const DEFAULT_FIELDS: JsonField[] = [
    { key: 'id', type: 'number' },
    { key: 'name', type: 'string' },
    { key: 'active', type: 'boolean' },
];

const STRING_PREFIXES = [
    'user', 'item', 'product', 'order', 'customer', 'address', 'city', 'country',
    'description', 'title', 'name', 'first_name', 'last_name', 'email', 'phone'
];

const STRING_SUFFIXES = [
    'name', 'title', 'description', 'address', 'city', 'country', 'email', 'phone',
    'id', 'code', 'number', 'value', 'status', 'type'
];

function generateRandomString(prefix = '') {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = prefix;

    // If no prefix, use a random one
    if (!prefix) {
        const randomPrefix = STRING_PREFIXES[Math.floor(Math.random() * STRING_PREFIXES.length)];
        result = randomPrefix + '_';
    }

    // Add random characters
    const length = 3 + Math.floor(Math.random() * 5);
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Add a random suffix
    if (Math.random() > 0.5) {
        result += '_' + STRING_SUFFIXES[Math.floor(Math.random() * STRING_SUFFIXES.length)];
    }

    return result;
}

function generateRandomValue(field: JsonField, depth = 0): JsonValue {
    switch (field.type) {
        case 'string':
            return generateRandomString(field.key);
        case 'number':
            return Math.floor(Math.random() * 1000);
        case 'boolean':
            return Math.random() > 0.5;
        case 'null':
            return null;
        case 'object':
            const obj: JsonObject = {};
            (field.children || []).forEach(childField => {
                obj[childField.key] = generateRandomValue(childField, depth + 1);
            });
            return obj;
        case 'array':
            const count = field.items || 3;
            return Array.from({ length: count }, () => {
                if (field.children?.length) {
                    const itemField = { ...field.children[0], key: 'item' };
                    return generateRandomValue(itemField, depth + 1);
                }
                return generateRandomValue({ ...field, type: 'string' }, depth + 1);
            });
        default:
            return null;
    }
}

function generateJson(fields: JsonField[]): JsonObject {
    const result: JsonObject = {};
    fields.forEach(field => {
        result[field.key] = generateRandomValue(field);
    });
    return result;
}

function JsonFieldEditor({ field, onChange, onDelete, onAddChild, depth = 0 }: {
    field: JsonField;
    onChange: (field: JsonField) => void;
    onDelete: () => void;
    onAddChild: () => void;
    depth?: number;
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = field.type === 'object' || field.type === 'array';

    return (
        <div className="space-y-6 h-full flex flex-col">
            <div className="flex items-center space-x-2">
                {hasChildren && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </Button>
                )}
                {!hasChildren && <div className="w-6" />}

                <Input
                    value={field.key}
                    onChange={(e) => onChange({ ...field, key: e.target.value })}
                    className="w-40"
                    placeholder="Field name"
                />

                <select
                    value={field.type}
                    onChange={(e) => {
                        const newType = e.target.value as JsonField['type'];
                        onChange({
                            ...field,
                            type: newType,
                            children: (newType === 'object' || newType === 'array') ? (field.children || []) : undefined
                        });
                    }}
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="null">Null</option>
                    <option value="object">Object</option>
                    <option value="array">Array</option>
                </select>

                {field.type === 'array' && (
                    <Input
                        type="number"
                        min="1"
                        max="20"
                        value={field.items || 3}
                        onChange={(e) => onChange({ ...field, items: parseInt(e.target.value) || 3 })}
                        className="w-20"
                        placeholder="Items"
                    />
                )}

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onDelete}
                    className="text-destructive hover:text-destructive"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>

                {(field.type === 'object' || field.type === 'array') && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onAddChild}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {hasChildren && isExpanded && field.children && (
                <div className="pl-8 space-y-2 border-l-2 border-muted">
                    {field.children.map((child, index) => (
                        <JsonFieldEditor
                            key={index}
                            field={child}
                            onChange={(updated) => {
                                const newChildren = [...field.children!];
                                newChildren[index] = updated;
                                onChange({ ...field, children: newChildren });
                            }}
                            onDelete={() => {
                                const newChildren = [...field.children!];
                                newChildren.splice(index, 1);
                                onChange({ ...field, children: newChildren });
                            }}
                            onAddChild={() => {
                                const newChildren = [...field.children!];
                                newChildren.push({ key: 'newField', type: 'string' });
                                onChange({ ...field, children: newChildren });
                            }}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function RandomJsonGenerator() {
    const [isClient, setIsClient] = useState(false);
    const [fields, setFields] = useState<JsonField[]>([]);
    const [output, setOutput] = useState<string>('');
    const [isArray, setIsArray] = useState(false);
    const [count, setCount] = useState(3);
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');
    const { toast } = useToast();

    // Initialize fields on client-side only
    useEffect(() => {
        setIsClient(true);
        setFields(JSON.parse(JSON.stringify(DEFAULT_FIELDS)));
    }, []);

    const generateJsonOutput = useCallback(() => {
        try {
            if (isArray) {
                const result = Array.from({ length: count }, () => generateJson(fields));
                setOutput(JSON.stringify(result, null, 2));
            } else {
                const result = generateJson(fields);
                setOutput(JSON.stringify(result, null, 2));
            }
        } catch (error) {
            setOutput(`Error generating JSON: ${error}`);
        }
    }, [fields, isArray, count]);

    const copyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'JSON has been copied to clipboard.',
            });
            setTimeout(() => setCopied(false), 2000);
        });
    }, [output, toast]);

    const addField = useCallback(() => {
        setFields([...fields, { key: `field_${fields.length + 1}`, type: 'string' }]);
    }, [fields]);

    const updateField = useCallback((index: number, field: JsonField) => {
        const newFields = [...fields];
        newFields[index] = field;
        setFields(newFields);
    }, [fields]);

    const deleteField = useCallback((index: number) => {
        const newFields = [...fields];
        newFields.splice(index, 1);
        setFields(newFields);
    }, [fields]);

    const addChildField = useCallback((parentIndex: number) => {
        const newFields = [...fields];
        const parent = newFields[parentIndex];

        if (!parent.children) {
            parent.children = [];
        }

        parent.children.push({ key: `field_${parent.children.length + 1}`, type: 'string' });
        setFields(newFields);
    }, [fields]);

    // Generate JSON when fields change (client-side only)
    useEffect(() => {
        if (isClient && fields.length > 0) {
            generateJsonOutput();
        }
    }, [fields, isArray, count, generateJsonOutput, isClient]);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
                <ToolPageIntro title="Random JSON Generator">
                    Generate random JSON data for testing and development. Define your structure with custom fields and types, then get sample objects or arrays instantly. Ideal for mock APIs, fixtures, and demos—no sign-up required.
                </ToolPageIntro>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>JSON Structure</CardTitle>
                            <CardDescription>Define the structure of your JSON data</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-x-2">
                                    <Label>Output as:</Label>
                                    <select
                                        value={isArray ? 'array' : 'object'}
                                        onChange={(e) => setIsArray(e.target.value === 'array')}
                                        className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    >
                                        <option value="object">Single Object</option>
                                        <option value="array">Array of Objects</option>
                                    </select>

                                    {isArray && (
                                        <>
                                            <Label className="ml-2">Count:</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={count}
                                                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                                                className="w-20 inline-block ml-2"
                                            />
                                        </>
                                    )}
                                </div>

                                <Button onClick={addField} size="sm">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Field
                                </Button>
                            </div>

                            <div className="space-y-2">
                                {isClient && fields.map((field, index) => (
                                    <JsonFieldEditor
                                        key={`${field.key}-${index}`}
                                        field={field}
                                        onChange={(updated) => updateField(index, updated)}
                                        onDelete={() => deleteField(index)}
                                        onAddChild={() => addChildField(index)}
                                    />
                                ))}
                                {isClient && fields.length === 0 && (
                                    <div className="text-center py-4 text-muted-foreground">
                                        No fields defined. Click "Add Field" to get started.
                                    </div>
                                )}
                            </div>
                        </CardContent>
                        <CardFooter className="flex justify-end">
                            <Button onClick={generateJsonOutput}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Regenerate
                            </Button>
                        </CardFooter>
                    </Card>

                    <div className="space-y-6">
                        <Card className="h-full">
                            <CardHeader>
                                <div className="space-y-2">
                                    <CardTitle>Generated JSON</CardTitle>
                                    <div className="flex justify-between items-center">
                                        <Tabs
                                            value={activeTab}
                                            onValueChange={setActiveTab}
                                            className="w-full"
                                        >
                                            <TabsList>
                                                <TabsTrigger value="preview">Preview</TabsTrigger>
                                                <TabsTrigger value="raw">Raw</TabsTrigger>
                                            </TabsList>
                                        </Tabs>

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={copyToClipboard}
                                            disabled={!output}
                                            className="ml-4"
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4 mr-2 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4 mr-2" />
                                            )}
                                            Copy
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="flex-1 min-h-[300px] overflow-hidden flex flex-col">
                                <Tabs value={activeTab} className="flex-1 flex flex-col">
                                    <TabsContent value="preview" className="flex-1 overflow-hidden">
                                        <div className="h-full overflow-auto p-4 bg-muted/50 rounded-md">
                                            {output ? (
                                                <pre className="text-sm">
                                                    <code>
                                                        {output}
                                                    </code>
                                                </pre>
                                            ) : (
                                                <div className="text-muted-foreground text-center py-8">
                                                    <Code className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                    <p>Your generated JSON will appear here</p>
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="raw" className="flex-1 overflow-hidden">
                                        <div className="h-full">
                                            <Textarea
                                                value={output}
                                                onChange={(e) => setOutput(e.target.value)}
                                                className="h-full min-h-[300px] font-mono text-sm"
                                                placeholder="Your JSON will appear here..."
                                            />
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>

                    </div>
                </div>
            </div>
            <Card className="mt-6">
                <CardHeader className="pb-4">
                    <CardTitle className="text-xl">How to Use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-0">
                    <div className="space-y-4">
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-medium">1</span>
                            </div>
                            <div>
                                <h3 className="font-medium text-base">Define Your Structure</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Add fields to your JSON structure and set their types. Create nested objects and arrays by clicking the + button.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4 pt-2">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-medium">2</span>
                            </div>
                            <div>
                                <h3 className="font-medium text-base">Customize Output</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Toggle between a single object or an array of objects. For arrays, adjust the count using the input field.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4 pt-2">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-medium">3</span>
                            </div>
                            <div>
                                <h3 className="font-medium text-base">Copy & Use</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Use the copy button to get the generated JSON. The preview updates automatically as you make changes.
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Informational Content */}
            <InformationalContent
                title={randomJsonGeneratorContent.title}
                description={randomJsonGeneratorContent.description}
                content={randomJsonGeneratorContent.content}
                faqs={randomJsonGeneratorContent.faqs}
                relatedTools={randomJsonGeneratorContent.relatedTools}
            />

            <Toaster />
        </div >
    );
}
